import { FC, useContext, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { $agentsHistory, fetchAgentThreadsEvent } from './agent-store';
import { useOpenMenuContextAgentThread } from './agent-thread-context-menu';
import { ChatbotContext } from './chatbot-context';
import { ChatbotThread } from './types';

function formatHistoryItemDate(timestamp: number): string {
  const dateToFormat = dayjs(timestamp);
  const now = dayjs();
  const yesterday = now.subtract(1, 'day');

  if (dateToFormat.isSame(now, 'day')) {
    return dateToFormat.format('[today at] HH:mm');
  } else if (dateToFormat.isSame(yesterday, 'day')) {
    return dateToFormat.format('[yesterday at] HH:mm');
  } else {
    return dateToFormat.format('MM/DD/YYYY [at] HH:mm');
  }
}

enum ThreadGroup {
  TODAY,
  YESTERDAY,
  LAST_WEEK,
  LAST_MONTH,
  EARLIER,
}

const threadGroupsParams: Record<
  ThreadGroup,
  {
    title: string;
    filter: (thread: ChatbotThread) => boolean;
  }
> = {
  [ThreadGroup.TODAY]: {
    title: 'Today',
    filter: (thread) => dayjs(thread.updatedAt).isSame(dayjs(), 'day'),
  },
  [ThreadGroup.YESTERDAY]: {
    title: 'Yesterday',
    filter: (thread) => dayjs(thread.updatedAt).isSame(dayjs().subtract(1, 'day'), 'day'),
  },
  [ThreadGroup.LAST_WEEK]: {
    title: 'Last week',
    filter: (thread) => {
      const updated = dayjs(thread.updatedAt);
      const now = dayjs();
      return updated.isBefore(now.subtract(1, 'day'), 'day') && updated.isAfter(now.subtract(1, 'week'), 'day');
    },
  },
  [ThreadGroup.LAST_MONTH]: {
    title: 'Last month',
    filter: (thread) => {
      const updated = dayjs(thread.updatedAt);
      const now = dayjs();
      return updated.isBefore(now.subtract(1, 'week'), 'day') && updated.isAfter(now.subtract(1, 'month'), 'day');
    },
  },
  [ThreadGroup.EARLIER]: {
    title: 'Earlier',
    filter: (thread) => {
      const updated = dayjs(thread.updatedAt);
      const now = dayjs();
      return updated.isBefore(now.subtract(1, 'month'), 'day');
    },
  },
};

type ChatbotAgentHistoryProps = {
  onThreadClick: (threadId: string) => void;
  showTitle?: boolean;
  hideItemsContextMenu?: boolean;
};

export const ChatbotAgentHistory: FC<ChatbotAgentHistoryProps> = ({
  onThreadClick,
  showTitle,
  hideItemsContextMenu,
}: ChatbotAgentHistoryProps) => {
  const [agentsHistory] = useUnit([$agentsHistory]);
  const [fetchAgentThreads] = useUnit([fetchAgentThreadsEvent]);

  const aiContext = useContext(ChatbotContext);

  useEffect(() => {
    if (aiContext.agentId) {
      fetchAgentThreads({ agentId: aiContext.agentId, orgId: aiContext.orgId ?? '' });
    }
  }, [aiContext.agentId, fetchAgentThreads, aiContext.orgId]);

  const agentHistory = useMemo(() => {
    return aiContext.agentId ? agentsHistory[aiContext.agentId ?? ''] : undefined;
  }, [agentsHistory, aiContext.agentId]);

  const threadsGroups: Record<ThreadGroup, ChatbotThread[]> = useMemo(() => {
    const groups = Object.values(ThreadGroup).filter((value) => typeof value === 'number') as ThreadGroup[];
    return groups.reduce(
      (acc: Record<ThreadGroup, ChatbotThread[]>, group) => {
        acc[group] = (agentHistory?.threads ?? []).filter(threadGroupsParams[group].filter);
        return acc;
      },
      {
        [ThreadGroup.TODAY]: [],
        [ThreadGroup.YESTERDAY]: [],
        [ThreadGroup.LAST_WEEK]: [],
        [ThreadGroup.LAST_MONTH]: [],
        [ThreadGroup.EARLIER]: [],
      }
    );
  }, [agentHistory]);

  if (!aiContext.agentId) {
    return null;
  }

  let content = null;
  const ready = !(agentHistory?.status === 'initial_loading' || agentHistory?.agentId !== aiContext.agentId);
  if (!ready) {
    content = <AiAgentHistorySkeleton />;
  } else if (agentHistory?.status === 'error') {
    content = <div>Error occured while fetching history</div>;
  } else if (!agentHistory?.threads?.length) {
    content = (
      <div
        className={classNames('w-full flex items-center justify-center', {
          'mt-[180px]': aiContext.pageMode,
        })}
      >
        <div className="flex flex-col items-center justify-center">
          <div className="w-[65px] h-[65px] flex items-center justify-center">
            <Icon icon={IconsMap.AI_AGENT_CHATS} />
          </div>
          <div className="w-[234px] mt-6 text-center text-base font-bold">No history of usage yet</div>
          <div className="w-[234px] mt-1 text-center text-sm text-neutral-500">
            Your chats with AI agents will appear here after you start conversation
          </div>
        </div>
      </div>
    );
  } else {
    // TODO: list lazy loading?
    content = (
      <div className="w-full flex flex-col justify-start gap-8">
        {['loaded', 'updating'].includes(agentHistory?.status ?? '') &&
          Object.keys(threadsGroups).map((rawKey) => {
            const key = Number(rawKey) as ThreadGroup;
            const threads = threadsGroups[key];
            const title = threadGroupsParams[key].title;
            return threads.length === 0 ? null : (
              <div>
                <div className="uppercase text-[10px] flex items-center h-[36px]">{title}</div>
                <div className="w-full flex flex-col gap-2">
                  {threads.map((thread) => (
                    <AiAgentHistoryItem
                      thread={thread}
                      onThreadClick={() => onThreadClick(thread.id ?? '')}
                      hideContextMenuIcon={hideItemsContextMenu}
                      key={thread.id ?? ''}
                    />
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    );
  }
  return (
    <div className="w-full h-full overflow-y-scroll p-4 mb-5">
      {showTitle && ready && <div className="text-sm font-bold h-[56px] mb-4 flex items-center">History of usage</div>}
      {content}
    </div>
  );
};

const AiAgentHistoryItem = (props: {
  thread: ChatbotThread;
  onThreadClick: () => void;
  hideContextMenuIcon?: boolean;
}) => {
  const { thread } = props;

  const aiContext = useContext(ChatbotContext);
  const openContextMenu = useOpenMenuContextAgentThread();

  return (
    <div
      className={classNames(
        'w-full h-[96px] p-4',
        'border rounded-md',
        'flex justify-start gap-2',
        'hover:bg-neutral-100 cursor-pointer'
      )}
      onClick={props.onThreadClick}
    >
      <div className="flex flex-col justify-between grow h-full">
        <div className="w-full h-[2lh] text-sm line-clamp-2">{thread.summary ?? ''}</div>
        <div className="w-full flex justify-start text-xs text-neutral-500">
          {formatHistoryItemDate(thread.updatedAt)}
        </div>
      </div>
      {!props.hideContextMenuIcon && (
        <div
          className={classNames(
            'min-w-7 min-h-7 w-7 h-7 flex-none',
            'hover:bg-neutral-200 cursor-pointer rounded-full',
            'flex items-center justify-center'
          )}
          onClick={(e) => {
            e.stopPropagation();
            openContextMenu(
              {
                agentId: aiContext.agentId ?? '',
                threadId: thread.id ?? '',
                orgId: aiContext.orgId ?? '',
              },
              e
            );
          }}
        >
          <Icon icon={IconsMap.MORE_VERTICAL} className="w-5 h-5 text-neutral-500" />
        </div>
      )}
    </div>
  );
};

const AiAgentHistorySkeleton = () => {
  const baseColor = 'rgb(245, 245, 245)';
  const highlightColor = '#fff';

  const itemsBlock = (n: number) => (
    <div className="flex flex-col gap-2">
      {Array.from({ length: n }).map((_, index) => (
        <Skeleton baseColor={baseColor} highlightColor={highlightColor} height={76} key={index} />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-[42px]">
      <div>
        <Skeleton baseColor={baseColor} highlightColor={highlightColor} height={28} width={137} className="mb-6" />
        <Skeleton baseColor={baseColor} highlightColor={highlightColor} height={28} width={137} className="mb-3.5" />
        {itemsBlock(3)}
      </div>
      <div>
        <Skeleton baseColor={baseColor} highlightColor={highlightColor} height={28} width={137} className="mb-3.5" />
        {itemsBlock(3)}
      </div>
    </div>
  );
};
